import { useState } from 'react'
import { useReselector } from '@/utils/sharedHooks'

import { selectSampleTaxCalculation } from '@/selectors/userTaxCalculation.selectors'
import { usePlanChangeEntry } from '@/features/user/settings/sections/plan-change/lib/usePlanChangeEntry'
import {
  faArrowRight,
  faArrowUpRightFromSquare,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Text,
  Button,
  Card,
  Link,
  Icon,
  Dialog,
  Image,
  Alert,
  Banner,
  BannerVariants,
  CardColors,
} from '@/ui'

import { selectPlanNameOrBasic } from '@/reducers/subscription.slice'
import { TextColors, TypographyStyles } from '@/styles'
import EstimateBreakdownV3 from './components/estimate-breakdown/estimate-breakdown-v3'
import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { ProductFeatureId } from '@/actions/permissionActions'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'

export const BasicPlanQTEUpsellBanner = () => {
  const hasQTEPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.quarterlyTaxes
  )
  const planName = useReselector(selectPlanNameOrBasic)
  const { goToPlanChange } = usePlanChangeEntry()

  if (hasQTEPermission) {
    return null
  }

  return (
    <Banner
      title={`You're currently on a ${planName} Subscription Plan.`}
      text="Upgrade your plan and get quarterly tax estimates, annual tax preparation and more."
      variant={BannerVariants.ANNOUNCEMENT}
      ctaText="Explore Plans"
      onClick={goToPlanChange}
    />
  )
}

const ILLUSTRATION_WRAPPER_CLASSES = 'shrink-0 w-45 h-45'
const ILLUSTRATION_CLASSES = 'w-full h-full'
const CARD_CONTENT_CLASSES = 'flex flex-col md:flex-row gap-6 items-start'

const BasicPlanQTEInfoCard = () => {
  return (
    <Card color={CardColors.LEVEL_1}>
      <div className={CARD_CONTENT_CLASSES}>
        <div className={ILLUSTRATION_WRAPPER_CLASSES}>
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/bank-auto.svg"
            className={ILLUSTRATION_CLASSES}
            objectFit="contain"
            alt="Quarterly Taxes Illustration"
          />
        </div>

        <div className="flex-1 space-y-4">
          <Text variant={TypographyStyles.DISPLAY_XS} color={TextColors.HEADER}>
            What are quarterly taxes?
          </Text>

          <Text variant={TypographyStyles.BODY_MD}>
            As a therapy practice owner, there&apos;s a very good chance you
            need to pay quarterly taxes. Rather than paying the IRS all the
            taxes you owe them in one lump sum at the end of the year, you pay
            it to them gradually, in quarterly installments.
          </Text>

          <Text variant={TypographyStyles.BODY_MD}>
            You&apos;ll need to figure out how to estimate the taxes you owe,
            and factor quarterly payments into your budget. Check out the
            following resources to help you calculate an estimate.
          </Text>

          <div className="flex flex-col gap-2">
            <Link
              href="https://www.joinheard.com/articles/how-to-pay-quarterly-estimated-taxes-for-your-therapy-practice"
              newPage
            >
              Read article{' '}
              <Icon icon={faArrowUpRightFromSquare} className="ml-1 inline" />
            </Link>
            <Link
              href="https://www.joinheard.com/events/navigating-quarterly-taxes-as-a-therapist"
              newPage
            >
              Watch video{' '}
              <Icon icon={faArrowUpRightFromSquare} className="ml-1 inline" />
            </Link>
          </div>
        </div>
      </div>
    </Card>
  )
}

const BasicPlanQTEUpsellCard = () => {
  const [exampleBreakdownModalOpen, setExampleBreakdownModalOpen] =
    useState(false)
  const sampleCalculation = useReselector(selectSampleTaxCalculation)
  const estimateDetails = useReselector(selectActiveQuarterlyTaxEstimateDetails)

  const { goToPlanChange } = usePlanChangeEntry()

  return (
    <>
      <Card color={CardColors.LEVEL_0}>
        <div className={CARD_CONTENT_CLASSES}>
          <div className={ILLUSTRATION_WRAPPER_CLASSES}>
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/calculator2.svg"
              className={ILLUSTRATION_CLASSES}
              objectFit="contain"
              alt="Calculator illustration"
            />
          </div>

          <div className="flex-1 space-y-4">
            <Text
              variant={TypographyStyles.DISPLAY_XS}
              color={TextColors.HEADER}
            >
              Want quarterly taxes estimated for you?
            </Text>

            <Text variant={TypographyStyles.BODY_MD}>
              Upgrade your plan and we&apos;ll help you calculate your quarterly
              tax estimates. We&apos;ll gather information from you to calculate
              an estimate, and provide a detailed breakdown of how we got to
              that number.
            </Text>

            <div className="flex flex-col items-start gap-4">
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setExampleBreakdownModalOpen(true)
                }}
              >
                View breakdown example{' '}
                <Icon icon={faArrowRight} className="ml-1 inline" />
              </Link>

              <Button onClick={goToPlanChange}>Explore Plans</Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog
        open={exampleBreakdownModalOpen}
        onOpenChange={setExampleBreakdownModalOpen}
      >
        <Dialog.Content className="max-w-full">
          <div className="flex flex-col gap-2">
            <Alert variant="warning">
              <Text variant={TypographyStyles.H2}>Using sample data</Text>
            </Alert>
          </div>

          <EstimateBreakdownV3
            taxCalc={sampleCalculation}
            estimateDetails={estimateDetails}
            isSample
          />
          <Dialog.Close>
            <Button variant="secondary">
              <Icon icon={faXmark} />
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export const BasicPlanQTEUpsell = () => {
  return (
    <div className="flex flex-col gap-4">
      <BasicPlanQTEInfoCard />
      <BasicPlanQTEUpsellCard />
    </div>
  )
}
